import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'

/**
 * Cloud STTターン状態の列挙型
 */
export enum CloudSttTurnState {
  IDLE = 'IDLE',
  LISTENING = 'LISTENING',
  TRANSCRIBING = 'TRANSCRIBING',
  WAITING_AI = 'WAITING_AI',
  COMPLETED = 'COMPLETED',
}

/**
 * Cloud STTターンステートマシンサービス
 *
 * @description
 * Cloud STTモードにおけるターンライフサイクル状態を管理する有限ステートマシン。
 * 5つの状態（IDLE、LISTENING、TRANSCRIBING、WAITING_AI、COMPLETED）と
 * それらの間の許可された遷移を定義し、無効な遷移を防止する。
 *
 * ターンライフサイクルフロー:
 * 1. IDLE: セッション開始前またはターン間の待機状態
 * 2. LISTENING: ユーザーターン開始、音声ストリーミング中
 * 3. TRANSCRIBING: 暫定トランスクリプト受信中、ユーザー発話中
 * 4. WAITING_AI: userTurnComplete受信後、AI応答待ち
 * 5. COMPLETED: aiTurnComplete受信後、次のターン準備完了待ち
 *
 * @responsibilities
 * - Cloud STTターン状態の遷移ロジック管理
 * - 状態遷移バリデーション（許可された遷移のみ実行）
 * - 状態変化のObservable発行
 * - ターンライフサイクル全体の状態追跡
 *
 * @dependencies
 * - RxJS BehaviorSubject（状態ストリーム管理）
 *
 * @example
 * ```typescript
 * constructor(private turnSM: CloudSttTurnStateMachineService) {}
 *
 * ngOnInit() {
 *   // 状態変化を購読
 *   this.turnSM.getCurrentState().subscribe(state => {
 *     console.log('現在のターン状態:', state);
 *   });
 *
 *   // ターン開始
 *   if (this.turnSM.transition(CloudSttTurnState.LISTENING)) {
 *     console.log('ユーザーターンを開始しました');
 *   }
 *
 *   // 暫定トランスクリプト受信
 *   if (this.turnSM.transition(CloudSttTurnState.TRANSCRIBING)) {
 *     console.log('ユーザー発話中');
 *   }
 * }
 * ```
 *
 * @see design.md CloudSttTurnStateMachineService section
 * @see requirements.md 要件4.6: Cloud STTターンライフサイクルのステートマシン化
 */
@Injectable({
  providedIn: 'root',
})
export class CloudSttTurnStateMachineService {
  /**
   * 現在の状態を保持するBehaviorSubject
   */
  private currentState$ = new BehaviorSubject<CloudSttTurnState>(
    CloudSttTurnState.IDLE,
  )

  /**
   * 状態遷移テーブル
   * 各状態から遷移可能な状態のリスト
   *
   * 遷移ルール:
   * - IDLE: ターン開始（LISTENING）のみ可能
   * - LISTENING: トランスクリプト受信（TRANSCRIBING）または中断（IDLE）が可能
   * - TRANSCRIBING: ターン完了（WAITING_AI）または中断（IDLE）が可能
   * - WAITING_AI: AI応答完了（COMPLETED）または中断（IDLE）が可能
   * - COMPLETED: 次のターン開始（LISTENING）またはアイドル戻り（IDLE）が可能
   */
  private readonly transitionTable: Record<
    CloudSttTurnState,
    CloudSttTurnState[]
  > = {
    [CloudSttTurnState.IDLE]: [CloudSttTurnState.LISTENING],
    [CloudSttTurnState.LISTENING]: [
      CloudSttTurnState.TRANSCRIBING,
      CloudSttTurnState.IDLE,
    ],
    [CloudSttTurnState.TRANSCRIBING]: [
      CloudSttTurnState.WAITING_AI,
      CloudSttTurnState.IDLE,
    ],
    [CloudSttTurnState.WAITING_AI]: [
      CloudSttTurnState.COMPLETED,
      CloudSttTurnState.IDLE,
    ],
    [CloudSttTurnState.COMPLETED]: [
      CloudSttTurnState.IDLE,
      CloudSttTurnState.LISTENING,
    ],
  }

  /**
   * 現在のCloud STTターン状態を取得
   *
   * @returns 状態のObservableストリーム
   */
  getCurrentState(): Observable<CloudSttTurnState> {
    return this.currentState$.asObservable()
  }

  /**
   * 新しい状態へ遷移
   *
   * @param newState 遷移先の状態
   * @returns 遷移成功ならtrue、無効遷移ならfalse
   * @throws Error 遷移失敗時にログ出力
   */
  transition(newState: CloudSttTurnState): boolean {
    const currentState = this.currentState$.value

    if (!this.canTransition(currentState, newState)) {
      console.warn(
        `[CloudSttTurnStateMachine] 無効な状態遷移を検出: ${currentState} → ${newState}`,
      )
      return false
    }

    this.currentState$.next(newState)
    console.log(
      `[CloudSttTurnStateMachine] 状態遷移: ${currentState} → ${newState}`,
    )
    return true
  }

  /**
   * 遷移が許可されているかチェック
   *
   * @param from 現在の状態
   * @param to 遷移先の状態
   * @returns 許可されていればtrue
   */
  canTransition(from: CloudSttTurnState, to: CloudSttTurnState): boolean {
    const allowedTransitions = this.transitionTable[from]
    return allowedTransitions.includes(to)
  }
}
